import { Injectable, inject } from "@angular/core";
import {
  Observable,
  forkJoin,
  map,
  of,
  catchError,
  throwError,
} from "rxjs";
import {
  ProfileService,
  UserProfile,
} from "src/app/pages/profile/profile.service";
import { ToastService } from "./toast.service";
import { TranslateService } from "@ngx-translate/core";
import { AppConfigService } from "src/app/core/appConfigs/app-config.service";
import { AppConfigs } from "src/app/core/appConfigs/app-config-model";
import { BillingService } from "src/app/pages/billing/billing.service";
import { BillingData } from "src/app/pages/billing/billing.model";
import { StoreService } from "src/app/services/apps/store/store.service";
import { ProviderService } from "src/app/services/apps/providers/provider.service";
import { ProviderClaimTypesService } from "src/app/services/apps/providers/provider-claim-types.service";
import { ProviderCaseStatusService } from "src/app/services/apps/providers/provider-case-status.service";
import { CountriesService } from "./countries.service";

/** Data loaded on login / loadInitialDataGuard (bootstrap only). */
export interface AppInitialData {
  userProfile: UserProfile | null;
  appConfigs: AppConfigs;
  billing: BillingData;
}

@Injectable({ providedIn: "root" })
export class AppInitialDataService {
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private billingService = inject(BillingService);
  private profileService = inject(ProfileService);
  private appConfigService = inject(AppConfigService);
  private storeService = inject(StoreService);
  private providerService = inject(ProviderService);
  private providerClaimTypesService = inject(ProviderClaimTypesService);
  private providerCaseStatusService = inject(ProviderCaseStatusService);
  private countriesService = inject(CountriesService);

  loadInitialData(): Observable<AppInitialData> {
    return forkJoin({
      userProfile: this.profileService.getUserProfile(),
      appConfigs: this.appConfigService.get(),
      billing: this.billingService.ensureBillingCached(),
    }).pipe(
      map(({ userProfile, appConfigs, billing }) => ({
        userProfile,
        appConfigs,
        billing,
      })),
      catchError((error) => {
        this.toast.error(
          this.translate.instant("toast_errors.loadInitialDataFailed"),
        );
        console.error("Error loading initial data:", error);
        return throwError(() => error);
      }),
    );
  }

  /** Profile, app config, and billing — catalog entities load on demand elsewhere. */
  ensureInitialDataLoaded(): Observable<void> {
    return this.ensureBootstrapDataLoaded({ includeBilling: true });
  }

  /** Ops Console entry: profile + app config only (no billing customer bootstrap). */
  ensureOpsInitialDataLoaded(): Observable<void> {
    return this.ensureBootstrapDataLoaded({ includeBilling: false });
  }

  private ensureBootstrapDataLoaded(options: {
    includeBilling: boolean;
  }): Observable<void> {
    const userProfile$ = this.profileService.userProfile()
      ? of(this.profileService.userProfile())
      : this.profileService.getUserProfile();
    const appConfigs$ = this.appConfigService.appConfigs()
      ? of(this.appConfigService.appConfigs()!)
      : this.appConfigService.get();

    const bootstrap$ = options.includeBilling
      ? forkJoin({
          userProfile: userProfile$,
          appConfigs: appConfigs$,
          billing: this.billingService.ensureBillingCached(),
        })
      : forkJoin({
          userProfile: userProfile$,
          appConfigs: appConfigs$,
        });

    return bootstrap$.pipe(
      map(() => undefined),
      catchError((error) => {
        console.error("Error loading initial app data", error);
        this.toast.error(
          this.translate.instant("toast_errors.loadInitialDataFailed"),
        );
        return throwError(() => error);
      }),
    );
  }

  cleanCaches(): void {
    this.storeService.clearCache();
    this.providerService.clearCache();
    this.providerClaimTypesService.clearCache();
    this.providerCaseStatusService.clearCache();
    this.profileService.clearCache();
    this.appConfigService.clearCache();
    this.countriesService.clearCache();
    this.billingService.clearCache();
  }
}
